import * as React from 'react';
import { useCallback, useEffect } from 'react';
import Range, { SliderProps } from 'rc-slider';
import { Tooltip } from './tooltip';
import 'rc-slider/assets/index.css'
import { setInInteraction, valueChanged, valueInteractionStarted } from '../../../store/weatherSearchSlice';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import _, { isArray, isNil } from 'lodash';
import { TemperatureUnitContext } from '../../layout/layout';
import { WeatherSearchKey, RangeProperties } from '../../../store/weatherSearchDefaults';
import { convertToDegrees, convertToFahrenheit } from '../../../services/convertUnits';
import { useLoc } from '../../../hooks/useLoc';
import { FormLabel } from './label';
import { checkPropertyMinMax } from '../../../services/weatherSearch';

export interface TemperatureRangeProps {
    prop: WeatherSearchKey,
    isMulti: boolean,
    className?: string,
    inverted?: boolean,
    useBlue?: boolean,
    propTitle: string
}

const tempProps = ['tmin', 'tmax'];
const sstProps = ['sst'];
const tempRange: [number, number] = [0, 30];
const sstRange: [number, number] = [16, 30];

export const TemperatureRange = ({ prop, isMulti, className, inverted, propTitle, useBlue }: TemperatureRangeProps) => {
    const [selectedUnit] = React.useContext(TemperatureUnitContext);

    const propertyUnit = useAppSelector(state => {
        return (state.weatherSearch.searchProperties[prop] as RangeProperties).units;
    });
    const isDegrees = propertyUnit === 'deg';
    const unitToShow = isDegrees ? ` °${selectedUnit}` : propertyUnit
    const minSelector = useAppSelector(state => {
        return (state.weatherSearch.searchProperties[prop] as RangeProperties).min;
    });
    const maxSelector = useAppSelector(state => {
        return (state.weatherSearch.searchProperties[prop] as RangeProperties).max;
    });
    const val = useAppSelector(state => {
        return (state.weatherSearch.searchProperties[prop] as RangeProperties).val;
    });
    const disabled = useAppSelector(state => {
        return (state.weatherSearch.searchProperties[prop] as RangeProperties).disabled;
    });
    const min = isDegrees ? convertToUnits(minSelector, selectedUnit) : minSelector;
    const max = isDegrees ? convertToUnits(maxSelector, selectedUnit) : maxSelector;

    const loc = useLoc();

    const dispatch = useAppDispatch();

    const [value, setValue] = React.useState(val);
    const [isUpdating, setIsUpdating] = React.useState(false);

    const isTemp = tempProps.includes(prop);
    const isSst = sstProps.includes(prop);
    const range = isTemp ? tempRange : sstRange;
    let gradient: [number, number] = undefined;
    if (isTemp || isSst) {
        gradient = calculateGradient(range, value as [number, number], selectedUnit);
    }
    const updateUnitsBeforeDispatch = (val: number[]) => {
        return val.map(v => {
            if (v == min) return minSelector;
            if (v == max) return maxSelector;
            return Math.round(convertToDegrees(v))
        })
    }
    const throttledDispatchValueChanged = useCallback(_.throttle((val: number | number[]) => {
        if (isDegrees && selectedUnit != 'C') {
            val = updateUnitsBeforeDispatch(val as number[]);
        }
        dispatch(valueChanged({ prop, val }));
    }, 200), [isUpdating]);

    const onBeforeChange = () => {
        setIsUpdating(true);
        dispatch(valueInteractionStarted(prop));
    }

    const onChange = (value: number | number[]) => {
        setValue(value);
        throttledDispatchValueChanged(value);
    }

    const onAfterChange = () => {
        setIsUpdating(false);
        dispatch(setInInteraction(false));
    }

    useEffect(() => {
        if (!isUpdating) {
            setValue(val);
        }
    }, [val]);
    useEffect(() => {
        if (!isUpdating && isDegrees) {
            setValue((val as number[]).map(v => convertToUnits(v, selectedUnit)));
        }
    }, [selectedUnit]);

    const handleRender: SliderProps['handleRender'] = (node, props) => {
        return (<>
            {node}
            {props.dragging && <Tooltip left={node.props.style.left} text={`${props.value}${unitToShow}`} />}
        </>
        );
    };
    const calculateValueToShow = (value: number | number[]) => {
        const { isMax, isMin, val } = checkPropertyMinMax(prop, { min, max, val: value })
        if (isArray(value)) {
            if (isMin && isMax) {
                return loc("common.doesNotMatter");
            }
            if (isMin) {
                return `< ${value[1]}${unitToShow}`;
            }
            if (isMax) {
                return `> ${value[0]}${unitToShow}`;
            }
            return `${value[0]}${unitToShow} - ${value[1]}${unitToShow}`
        }
        else if (isNil(val)) {
            return loc("common.doesNotMatter");
        }
        return `${inverted ? '> ' : '< '}${value}${unitToShow}`
    }
    const mainColor = useBlue ? "#5d87cd" : "#e84749";
    return (
        <div className={`flex flex-col ${className || ''} max-w-[600px] ${disabled && 'font-color-disabled'}`}>
            <FormLabel label={propTitle} />
            <div className='h-2' />
            <Range
                disabled={disabled}
                className='h-8'
                min={min}
                max={max}
                value={value}
                range={isMulti}
                handleRender={handleRender}
                allowCross={false}
                onBeforeChange={onBeforeChange}
                onChange={onChange}
                onAfterChange={onAfterChange}
                dotStyle={{ background: 'transparent', border: 'none' }}
                railStyle={
                    !disabled ? { background: inverted ? mainColor : '#e4e4e4' } : { background: '#e4e4e4' }
                }
                trackStyle={
                    !disabled ? {
                        background: (gradient) ? `linear-gradient(90deg, #3db7fd ${gradient[0]}%, #e84749 ${gradient[1]}%)`
                            : (inverted ? '#e4e4e4' : mainColor),
                    } : { background: '#e4e4e4' }
                }
            />
            <div className={`flex justify-between w-full mt-1`}>
                <div className="text-xs tracking-wide">{min}</div>
                {!disabled && <div className='font-b text-sm tracking-[0.4px] relative t-2'>{calculateValueToShow(value)}</div>}
                <div className="text-xs tracking-wide">{max}</div>
            </div>
        </div>
    )
}



const calculateGradient = (range: [number, number], vals: [number, number], units: string): [number, number] => {
    if (units != 'C') {
        vals = vals.map(v => Math.round(convertToDegrees(v))) as [number, number];
    }
    const diff = (vals[1] - vals[0]);
    const min = 0 - (vals[0] - range[0]) / diff * 100;
    const max = 100 + (range[1] - vals[1]) / diff * 100;
    return [Math.round(min), Math.round(max)];
}

function convertToUnits(val: number, units: string): number {
    if (units === 'C') {
        return val;
    }
    return Math.round(convertToFahrenheit(val));
}

